import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortal } from '@/contexts/PortalContext';
import { OFFICIAL_SEAL } from '@/lib/brand';
import { 
  LayoutDashboard, 
  BookOpen, 
  GraduationCap, 
  Calendar, 
  MessageSquare,
  Bot,
  Loader2,
  LogOut,
  Menu,
  X,
  FileText,
  Award,
  Bell,
  Mail
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

const studentNavItems = [
  { label: 'Dashboard', path: '/student/dashboard', icon: LayoutDashboard },
  { label: 'My Program', path: '/student/program', icon: GraduationCap },
  { label: 'My Courses', path: '/student/courses', icon: BookOpen },
  { label: 'Assignments', path: '/student/assignments', icon: FileText },
  // Phase A: Hidden until LMS feature wave is approved:
  //   Grades, Announcements (page), Messages, AI Tutor, Certificates
  //   Direct URLs still route to ComingSoon — only sidebar links are suppressed
  //   to remove credibility risk.
];

export default function StudentLayout() {
  const { isAuthenticated, loading: authLoading, user, logout, portalRole } = useAuth();
  const { portalProfile, loading: portalLoading } = usePortal();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Show loading state
  if (authLoading || portalLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-[#1E3A8A]">Loading portal...</p>
        </div>
      </div>
    );
  }

  // Redirect to unified login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verify role is student
  const actualRole = portalRole || portalProfile?.role || 'student';
  if (actualRole !== 'student') {
    // Redirect to correct dashboard
    if (actualRole === 'instructor') return <Navigate to="/instructor/dashboard" replace />;
    if (actualRole === 'admin') return <Navigate to="/admin/dashboard" replace />;
  }

  const isActive = (path) => {
    if (path === '/student/dashboard') {
      return location.pathname === '/student/dashboard' || location.pathname === '/student';
    }
    return location.pathname.startsWith(path);
  };

  const NavLink = ({ item }) => (
    <Link
      to={item.comingSoon ? '#' : item.path}
      onClick={(e) => {
        if (item.comingSoon) e.preventDefault();
        setSidebarOpen(false);
      }}
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
        ${isActive(item.path) 
          ? 'bg-blue-600 text-white' 
          : item.comingSoon 
            ? 'text-gray-400 cursor-not-allowed' 
            : 'text-gray-700 hover:bg-gray-100'
        }
      `}
    >
      <item.icon className="w-5 h-5" />
      <span className="font-medium">{item.label}</span>
      {item.comingSoon && (
        <span className="ml-auto text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded">Soon</span>
      )}
    </Link>
  );

  const SidebarContent = () => (
    <>
      {/* Institutional Wordmark */}
      <div className="p-4 border-b border-gray-200">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src={OFFICIAL_SEAL}
            alt="Zodiac Workforce Intelligence Institute" 
            className="h-12 w-auto object-contain drop-shadow-[0_3px_8px_rgba(15,30,71,0.18)]"
          />
          <div className="leading-tight">
            <div className="zodiac-heading text-[#0F1E47] font-bold text-base tracking-tight">ZODIAC</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#991B1B] font-semibold">
              Workforce Intelligence<sup className="text-[6px] ml-0.5 align-super">™</sup>
            </div>
          </div>
        </Link>
        <p className="text-xs text-blue-600 font-medium mt-3">Student Portal</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {studentNavItems.map((item) => (
          <NavLink key={item.path} item={item} />
        ))}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || 'S'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{user?.name || 'Student'}</p>
            <p className="text-xs text-blue-600 font-medium">Student</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start gap-2"
          onClick={() => {
            logout();
            window.location.href = '/login';
          }}
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`
          fixed inset-0 bg-black/50 z-40 lg:hidden
          transition-opacity duration-300 ease-in-out
          ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        shadow-xl lg:shadow-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Mobile Close Button */}
        <button 
          className="lg:hidden absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => setSidebarOpen(false)}
        >
          <X className="w-5 h-5" />
        </button>
        
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4 z-30">
          <button 
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900 text-sm">Student Portal</h1>
            </div>
          </div>
          {/* Mobile user avatar */}
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'S'}
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
